import { z } from 'zod'
import { apiFetch } from './client'

/**
 * Schema for review data
 */
export const ReviewSchema = z.object({
  id: z.number(),
  location_id: z.number().optional(),
  provider: z.enum(['google', 'internal']),
  external_review_id: z.string().nullable().optional(),
  canonical_review_id: z.string().nullable().optional(),
  author_name: z.string(),
  author_id: z.string().nullable().optional(),
  author_metadata: z
    .object({
      reviews_count: z.number().optional(),
      photos_count: z.number().optional(),
      is_local_guide: z.boolean().optional(),
    })
    .nullable()
    .optional(),
  rating: z.number().min(1).max(5),
  text: z.string().nullable().optional(),
  original_text: z.string().nullable().optional(),
  is_translated: z.boolean().default(false),
  language_code: z.string().nullable().optional(),
  published_at: z.string().nullable().optional(),
  raw_timestamp: z.string().nullable().optional(),
  owner_response: z.string().nullable().optional(),
  owner_response_at: z.string().nullable().optional(),
  synced_at: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  analysis: z
    .object({
      sentiment: z.string(),
      sentiment_score: z.number(),
      confidence: z.number(),
      language: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  has_draft: z.boolean().default(false),
})

export type Review = z.infer<typeof ReviewSchema>

/**
 * Schema for paginated reviews response
 */
export const ReviewsResponseSchema = z.object({
  reviews: z.array(ReviewSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  rating_counts: z.record(z.string(), z.number()).optional(),
})

export type ReviewsResponse = z.infer<typeof ReviewsResponseSchema>

/**
 * Draft tone options matching backend enum
 */
export type DraftTone = 'professional' | 'friendly' | 'apologetic' | 'brief'

/**
 * Schema for draft response
 */
export const DraftResponseSchema = z.object({
  review_id: z.number(),
  content: z.string(),
  tone: z.string(),
  created_at: z.string(),
})

export type DraftResponse = z.infer<typeof DraftResponseSchema>

/**
 * Trigger review scraping for a location.
 * Fire-and-forget — the job runs in the background via pgmq.
 */
export async function triggerReviewSync(
  locationId: number,
  googleMapsUrl: string,
  provider: 'internal' | 'google' = 'internal',
  maxReviews: number = 1000
): Promise<void> {
  await apiFetch(`/reviews/locations/${locationId}/sync`, {
    method: 'POST',
    body: JSON.stringify({
      google_maps_url: googleMapsUrl,
      provider,
      max_reviews: maxReviews,
    }),
  })
}

/**
 * Filter options for fetching reviews
 */
export interface ReviewFetchFilters {
  rating?: number
  sentiment?: 'positive' | 'neutral' | 'negative'
  datePreset?: 'today' | 'week' | 'month' | 'quarter'
  /** Inclusive start of an explicit range, as YYYY-MM-DD. */
  dateFrom?: string
  /** Inclusive end of an explicit range, as YYYY-MM-DD. */
  dateTo?: string
  responseStatus?: 'pending' | 'responded'
}

/**
 * Fetch reviews for a location
 */
export async function fetchLocationReviews(
  locationId: number,
  options?: {
    provider?: 'google' | 'internal'
    limit?: number
    offset?: number
    filters?: ReviewFetchFilters
  }
): Promise<ReviewsResponse> {
  const params = new URLSearchParams()
  if (options?.provider) params.set('provider', options.provider)
  if (options?.limit) params.set('limit', options.limit.toString())
  if (options?.offset) params.set('offset', options.offset.toString())

  // Add filter parameters
  if (options?.filters) {
    if (options.filters.rating) params.set('rating', options.filters.rating.toString())
    if (options.filters.sentiment) params.set('sentiment', options.filters.sentiment)
    if (options.filters.datePreset) params.set('date_preset', options.filters.datePreset)
    if (options.filters.dateFrom) params.set('date_from', options.filters.dateFrom)
    if (options.filters.dateTo) params.set('date_to', options.filters.dateTo)
    if (options.filters.responseStatus)
      params.set('response_status', options.filters.responseStatus)
  }

  const queryString = params.toString()
  const url = `/reviews/locations/${locationId}${queryString ? `?${queryString}` : ''}`

  const data = await apiFetch<ReviewsResponse>(url)
  return ReviewsResponseSchema.parse(data)
}

/**
 * Generate a draft response for a review
 *
 * @param language - 2-letter language code (e.g. 'en', 'es') or 'auto' to
 *                   let the backend pick from the review's detected language.
 */
export async function generateDraft(
  reviewId: number,
  tone: DraftTone,
  language?: string
): Promise<DraftResponse> {
  const body: { tone: DraftTone; language?: string } = { tone }
  if (language) body.language = language
  const data = await apiFetch<DraftResponse>(`/reviews/${reviewId}/draft`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
  return DraftResponseSchema.parse(data)
}

/**
 * Get existing draft for a review. Returns null if no draft exists.
 */
export async function getDraft(reviewId: number): Promise<DraftResponse | null> {
  const data = await apiFetch<DraftResponse | null>(`/reviews/${reviewId}/draft`)
  if (data === null) return null
  return DraftResponseSchema.parse(data)
}

/**
 * Update draft content for a review
 */
export async function updateDraft(reviewId: number, content: string): Promise<DraftResponse> {
  const data = await apiFetch<DraftResponse>(`/reviews/${reviewId}/draft`, {
    method: 'PATCH',
    body: JSON.stringify({ content }),
  })
  return DraftResponseSchema.parse(data)
}

/**
 * Sync status types
 */
export const LocationSyncStatusSchema = z.object({
  location_id: z.number(),
  review_count: z.number(),
  has_synced: z.boolean(),
})

export type LocationSyncStatus = z.infer<typeof LocationSyncStatusSchema>

export const BatchSyncStatusResponseSchema = z.object({
  locations: z.record(z.string(), LocationSyncStatusSchema),
})

export type BatchSyncStatusResponse = z.infer<typeof BatchSyncStatusResponseSchema>

/**
 * Schema for dashboard stats response
 */
export const DashboardStatsSchema = z.object({
  total_reviews: z.number(),
  average_rating: z.number(),
  rating_breakdown: z.record(z.string(), z.number()),
  responded_count: z.number(),
  response_rate: z.number(),
})

export type DashboardStats = z.infer<typeof DashboardStatsSchema>

/**
 * Fetch aggregated dashboard stats for a location
 */
export async function fetchDashboardStats(locationId: number): Promise<DashboardStats> {
  const data = await apiFetch<DashboardStats>(`/reviews/locations/${locationId}/stats`)
  return DashboardStatsSchema.parse(data)
}

/**
 * Fetch sync status for multiple locations
 */
export async function fetchBatchSyncStatus(
  locationIds: number[]
): Promise<Record<number, LocationSyncStatus>> {
  if (locationIds.length === 0) {
    return {}
  }

  const url = `/reviews/locations/sync-status?location_ids=${locationIds.join(',')}`
  const data = await apiFetch<BatchSyncStatusResponse>(url)
  const parsed = BatchSyncStatusResponseSchema.parse(data)

  // Convert string keys to numbers
  const result: Record<number, LocationSyncStatus> = {}
  for (const [key, value] of Object.entries(parsed.locations)) {
    result[parseInt(key, 10)] = value
  }
  return result
}
